import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddressbookPanelShell from "./AddressbookPanelShell";
import { apiGetPropertyAssetsCollection } from "../../../graphQL/properties/collections/propertyAssets";
import { toPropertyCard } from "../../properties/presenters";
import PropertyCard from "../../properties/panels/PropertyCard";
import { usePropertyCategories } from "../../properties/usePropertyCategories";

const PAGE_SIZE = 100;

const ContactPropertiesPanel = ({ contact, onClose, selectedId, onSelect, onOpenDetails, onAdd, refreshKey = 0 }) => {
  const categories = usePropertyCategories();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  const propertyIds = useMemo(() => {
    const raw = contact?.propertyListIds || contact?.data?.propertyListIds || [];
    return Array.isArray(raw)
      ? raw.map((v) => String(v || "").trim()).filter(Boolean)
      : [];
  }, [contact]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!contact?.id || propertyIds.length === 0) {
        setItems([]);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const result = await apiGetPropertyAssetsCollection({
          filters: { ids: propertyIds },
          pagination: { page: 1, limit: Math.max(PAGE_SIZE, propertyIds.length) },
          ordering: { field: "updatedAt", order: "Desc" },
        });
        if (cancelled) return;
        const fetched = Array.isArray(result?.items) ? result.items : [];
        setItems(fetched);
      } catch (err) {
        console.error("Failed to load contact properties:", err);
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [contact?.id, propertyIds.join("|"), refreshKey]);

  const cards = useMemo(() => items.map(toPropertyCard), [items]);

  const personName = (() => {
    const p = contact?.identity?.person || {};
    return `${p.firstName || ""} ${p.lastName || ""}`.trim() || contact?.identity?.company?.name || "Contact";
  })();

  return (
    <AddressbookPanelShell
      onClose={onClose}
      title={`Linked Properties · ${personName}`}
      headerActions={
        onAdd && contact?.id ? (
          <IconButton
            size="small"
            onClick={() => onAdd(contact)}
            title="Add property"
            sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" }, width: 24, height: 24, p: 0 }}
          >
            <AddIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
          </IconButton>
        ) : null
      }
      footerLeft={
        <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
          {propertyIds.length} linked
        </Typography>
      }
    >
      {!contact?.id ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12, color: "#64748B" }}>
            Select a contact to see linked properties.
          </Typography>
        </Box>
      ) : propertyIds.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12, color: "#64748B" }}>
            This contact is not linked to any properties.
          </Typography>
        </Box>
      ) : loading ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12, color: "#B91C1C" }}>
            Failed to load properties.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {cards.map((p, idx) => (
            <PropertyCard
              key={p?.id || `linked-property-${idx}`}
              property={p}
              number={idx + 1}
              categories={categories}
              isSelected={Boolean(selectedId) && p?.id === selectedId}
              onSelect={onSelect}
              onOpenDetails={onOpenDetails}
            />
          ))}
        </Box>
      )}
    </AddressbookPanelShell>
  );
};

export default ContactPropertiesPanel;
